import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SystemMetrics } from '@lms/shared-types';
import { api } from '@/lib/api';

const CIRCUMFERENCE = 2 * Math.PI * 40;

const GAUGE_CARDS = [
  { key: 'cpuUsagePercent' as const, label: 'CPU', icon: 'memory' },
  { key: 'memoryUsagePercent' as const, label: 'RAM', icon: 'memory_alt' },
  { key: 'diskUsagePercent' as const, label: 'Disco', icon: 'hard_drive' },
];

function circleOffset(percent: number) {
  const value = Math.min(100, Math.max(0, Number(percent ?? 0)));
  return CIRCUMFERENCE * (1 - value / 100);
}

function formatPercent(value: number) {
  return `${Number(value ?? 0).toFixed(1)}%`;
}

function formatTime(isoString?: string) {
  if (!isoString) return 'N/D';
  return new Date(isoString).toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

function detailFor(key: keyof SystemMetrics, metrics: SystemMetrics) {
  if (key === 'cpuUsagePercent') return 'Carga actual del procesador';
  if (key === 'memoryUsagePercent') return `${metrics.memoryUsed} usados de ${metrics.memoryTotal}`;
  if (key === 'diskUsagePercent') return `${metrics.diskUsed} usados de ${metrics.diskTotal}`;
  return '';
}

export function SystemStatus({
  initialMetrics,
  metricsUrl = '/health/metrics',
  className = 'lms-server-status__body',
}: {
  initialMetrics?: SystemMetrics;
  metricsUrl?: string;
  className?: string;
}) {
  const [refreshing, setRefreshing] = useState(false);

  const { data: metrics, refetch } = useQuery({
    queryKey: ['health-metrics'],
    queryFn: () => api<SystemMetrics>(metricsUrl),
    initialData: initialMetrics,
    refetchInterval: 5000,
  });

  const refreshNow = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  }, [refetch]);

  useEffect(() => {
    return () => setRefreshing(false);
  }, []);

  if (!metrics) return null;

  return (
    <div className={className}>
      <section className="lms-server-status__metrics" aria-labelledby="server-metrics-heading">
        <h2 className="sr-only" id="server-metrics-heading">
          Métricas críticas
        </h2>

        <div className="lms-server-status__gauges">
          {GAUGE_CARDS.map((card) => (
            <article key={card.key} className="lms-server-status__gauge-card">
              <div className="lms-server-status__gauge-label">
                <span className="material-symbols-outlined" aria-hidden="true">
                  {card.icon}
                </span>
                <span>{card.label}</span>
              </div>

              <div className="lms-server-status__gauge">
                <svg viewBox="0 0 100 100" aria-hidden="true">
                  <circle
                    className="lms-server-status__gauge-track"
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    strokeWidth="8"
                    strokeLinecap="round"
                  />
                  <circle
                    className="lms-server-status__gauge-progress"
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={CIRCUMFERENCE}
                    strokeDashoffset={circleOffset(metrics[card.key])}
                  />
                </svg>
                <div className="lms-server-status__gauge-value">
                  <span>{formatPercent(metrics[card.key])}</span>
                </div>
              </div>

              <p className="lms-server-status__gauge-detail">{detailFor(card.key, metrics)}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="lms-server-status__env" aria-labelledby="server-env-heading">
        <div className="lms-server-status__env-header">
          <span className="material-symbols-outlined" aria-hidden="true">
            dns
          </span>
          <h2 id="server-env-heading">Información del entorno</h2>
        </div>

        <div className="lms-server-status__env-grid">
          <div className="lms-server-status__env-item">
            <div className="lms-server-status__env-icon" aria-hidden="true">
              <span className="material-symbols-outlined">api</span>
            </div>
            <div>
              <h3>API (NestJS)</h3>
              <p>{metrics.apiVersion}</p>
            </div>
          </div>

          <div className="lms-server-status__env-item">
            <div className="lms-server-status__env-icon" aria-hidden="true">
              <span className="material-symbols-outlined">javascript</span>
            </div>
            <div>
              <h3>Node.js</h3>
              <p>v{metrics.nodeVersion}</p>
            </div>
          </div>

          <div className="lms-server-status__env-item">
            <div className="lms-server-status__env-icon" aria-hidden="true">
              <span className="material-symbols-outlined">database</span>
            </div>
            <div>
              <h3>Estado de Base de datos</h3>
              <div className="lms-server-status__db">
                <span
                  className={`lms-server-status__db-dot ${metrics.databaseConnected ? 'lms-server-status__db-dot--ok' : 'lms-server-status__db-dot--err'}`}
                  aria-hidden="true"
                />
                <p>{metrics.databaseConnected ? 'Conectada' : 'Desconectada'}</p>
              </div>
            </div>
          </div>

          <div className="lms-server-status__env-item">
            <div className="lms-server-status__env-icon" aria-hidden="true">
              <span className="material-symbols-outlined">terminal</span>
            </div>
            <div>
              <h3>Sistema Operativo</h3>
              <p>{metrics.os}</p>
            </div>
          </div>

          <div className="lms-server-status__env-item">
            <div className="lms-server-status__env-icon" aria-hidden="true">
              <span className="material-symbols-outlined">router</span>
            </div>
            <div>
              <h3>Host</h3>
              <p>{metrics.hostname}</p>
            </div>
          </div>

          <div className="lms-server-status__env-item lms-server-status__env-item--refresh">
            <div className="lms-server-status__env-icon" aria-hidden="true">
              <span className="material-symbols-outlined">schedule</span>
            </div>
            <div className="min-w-0 flex-1">
              <h3>Última actualización</h3>
              <p>{formatTime(metrics.capturedAt)}</p>
            </div>
            <button
              type="button"
              className="lms-server-status__refresh"
              title="Actualizar ahora"
              onClick={() => refreshNow()}
              disabled={refreshing}
            >
              <span
                className={`material-symbols-outlined ${refreshing ? 'lms-server-status__refresh-spin' : ''}`}
                aria-hidden="true"
              >
                refresh
              </span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
